'use client';

import React, { useState, useEffect, useRef } from 'react';
import TransitionLink from './TransitionLink';
import Image from 'next/image';
import type { IntegrationCategory } from '@/lib/get-navbar-data';

interface IntegrationsNavMenuProps {
  categories: IntegrationCategory[];
  lang: string;
  onClose: () => void;
  primaryTitle?: string;
  secondaryTitle?: string;
  viewAllButton?: string;
  totalCount?: number;
}

function stripNetSuite(title: string) {
  return title
    .replace(/\s*\+?\s*NetSuite Integration\s*/i, '')
    .replace(/\s+to\s+NetSuite Migration\s*/i, '')
    .trim();
}

export function IntegrationsNavMenu({ categories, lang, onClose, primaryTitle = 'Most Common', secondaryTitle = 'We Also Connect', viewAllButton = 'See All # Integrations', totalCount }: IntegrationsNavMenuProps) {
  const [activeTag, setActiveTag] = useState<string>(categories[0]?.tag ?? '');
  const [animKey, setAnimKey] = useState(0);
  const [indicator, setIndicator] = useState({ top: 0, height: 0 });
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const activeIndex = categories.findIndex((c) => c.tag === activeTag);
    const el = tabRefs.current[activeIndex];
    if (el) setIndicator({ top: el.offsetTop, height: el.offsetHeight });
  }, [activeTag, categories]);

  const activeCategory = categories.find((c) => c.tag === activeTag) ?? categories[0];

  if (!categories.length) return null;

  const featuredIntegrations = activeCategory?.integrations.slice(0, 6) ?? [];
  const moreIntegrations = activeCategory?.integrations.slice(6, 18) ?? [];

  return (
    <div className="grid grid-cols-[200px_1fr] gap-0">
      {/* Left column — category tabs */}
      <div className="relative flex flex-col border-r border-gray-200 pr-4">
        {/* Sliding indicator */}
        <span
          className="pointer-events-none absolute left-0 w-0.5 rounded-full bg-blue-500 transition-all duration-200 ease-in-out"
          style={{ top: indicator.top, height: indicator.height }}
        />
        {categories.map((cat, i) => (
          <button
            key={cat.tag}
            ref={(el) => { tabRefs.current[i] = el; }}
            type="button"
            onMouseEnter={() => { setActiveTag(cat.tag); setAnimKey((k) => k + 1); }}
            onClick={() => { setActiveTag(cat.tag); setAnimKey((k) => k + 1); }}
            className={[
              'px-4 py-2.5 text-left text-sm font-semibold transition-colors duration-150',
              activeTag === cat.tag
                ? 'text-gray-900'
                : 'text-gray-500 hover:text-gray-800',
            ].join(' ')}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Right column */}
      <div className="pl-6">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-gray-400">{primaryTitle}</p>
        {/* Featured cards — 2×3 grid */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {featuredIntegrations.map((integration, i) => (
            <TransitionLink
              key={`${animKey}-${integration.uid}`}
              href={`/${lang}/integrations/${integration.uid}`}
              onClick={onClose}
              style={{ animation: 'fadeInUp 0.3s ease-out both', animationDelay: `${i * 40}ms` }}
              className="group flex items-start gap-3 rounded-lg border border-transparent bg-white p-3 transition-all duration-200 hover:border-dashed hover:border-blue-400 hover:bg-transparent"
            >
              {integration.integration_mark?.url && (
                <div className="flex-shrink-0">
                  <Image
                    src={integration.integration_mark.url}
                    alt={integration.title}
                    width={32}
                    height={32}
                    className="h-8 w-8 rounded object-contain"
                  />
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate text-base font-medium text-gray-800">
                  {stripNetSuite(integration.title)}
                </p>
                {integration.integration_teaser && (
                  <p className="mt-0.5 line-clamp-2 text-sm text-gray-500">
                    {integration.integration_teaser}
                  </p>
                )}
              </div>
            </TransitionLink>
          ))}
        </div>

        {/* More integrations — expanding icon squares + see all link on same row */}
        {moreIntegrations.length > 0 && (
          <div className="mt-3 border-t border-gray-100 pt-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-gray-400">{secondaryTitle}</p>
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-wrap gap-1.5">
                {moreIntegrations.map((integration, i) => (
                  <TransitionLink
                    key={`${animKey}-more-${integration.uid}`}
                    href={`/${lang}/integrations/${integration.uid}`}
                    onClick={onClose}
                    className="group flex h-[46px] items-center overflow-hidden rounded-lg bg-white transition-[width] duration-200 ease-in-out"
                    style={{ width: '46px', animation: 'fadeInUp 0.5s ease-out both', animationDelay: `${(featuredIntegrations.length + i) * 40}ms` }}
                    onMouseEnter={(e) => {
                      const title = stripNetSuite(integration.title);
                      const approxWidth = Math.min(title.length * 8 + 58, 220);
                      (e.currentTarget as HTMLAnchorElement).style.width = `${approxWidth}px`;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.width = '46px';
                    }}
                  >
                    <div className="flex h-[46px] w-[46px] flex-shrink-0 items-center justify-center">
                      {integration.integration_mark?.url ? (
                        <Image
                          src={integration.integration_mark.url}
                          alt={integration.title}
                          width={28}
                          height={28}
                          className="h-8 w-8 rounded object-contain"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded bg-gray-200" />
                      )}
                    </div>
                    <span className="max-w-0 overflow-hidden whitespace-nowrap text-sm font-semibold text-gray-800 transition-[max-width] duration-200 ease-in-out group-hover:max-w-[180px] group-hover:pr-2">
                      {stripNetSuite(integration.title)}
                    </span>
                  </TransitionLink>
                ))}
              </div>

              {totalCount != null && (
                <TransitionLink
                  href={`/${lang}/integrations#matrix`}
                  onClick={onClose}
                  className="group flex flex-shrink-0 items-center gap-1 text-sm font-semibold text-blue-500 transition-colors duration-150 hover:text-blue-700"
                >
                  {viewAllButton.replace('#', String(totalCount))}
                  <span className="transition-transform duration-150 group-hover:translate-x-0.5">&rsaquo;</span>
                </TransitionLink>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
